import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environmentCAS';

export interface AgendaCas {
  id: number;
  userId: number;
  ownerId?: number;
  ownerName?: string;
  googleEventId?: string;
  title?: string;
  description?: string;
  startAt?: string;
  endAt?: string;
  civilite?: string;
  nom?: string;
  prenom?: string;
  telephone?: string;
  email?: string;
  status: string;
  nomVendeur?: string;
  origine?: string;
  projectId?: string;
  commentaire?: string;
  rawPayload?: string;
  isArchived?: boolean;
  archivedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PaginatedAgendaCasResponse {
  items: AgendaCas[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface AgendaCasConversionResponse {
  message: string;
  agendaProject: AgendaCas;
  projectId: string;
}

@Injectable({
  providedIn: 'root'
})
export class AgendaCasService {
  private apiUrl = `${environment.apiUrl}/api/agenda-cas`;

  constructor(private http: HttpClient) {}

  getAgendaProjects(limit: number = 10, offset: number = 0, filters?: { search?: string; status?: string; today_only?: boolean; user_id?: number; archived?: boolean }): Observable<PaginatedAgendaCasResponse> {
    let params = new HttpParams()
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    if (filters?.search) {
      params = params.set('search', filters.search);
    }

    if (filters?.status) {
      params = params.set('status', filters.status);
    }

    if (filters?.today_only) {
      params = params.set('today_only', 'true');
    }

    if (filters?.user_id != null) {
      params = params.set('user_id', filters.user_id.toString());
    }

    if (filters?.archived !== undefined) {
      params = params.set('archived', filters.archived.toString());
    }

    return this.http.get<PaginatedAgendaCasResponse>(this.apiUrl, { params }).pipe(
      map(response => {
        // Map backend snake_case to frontend camelCase
        if (response && response.items) {
          response.items = response.items.map(item => ({
            ...item,
            userId: (item as any).user_id || item.userId,
            ownerId: (item as any).owner_id || item.ownerId,
            ownerName: (item as any).owner_name || item.ownerName,
            googleEventId: (item as any).google_event_id || item.googleEventId,
            startAt: (item as any).start_at || item.startAt,
            endAt: (item as any).end_at || item.endAt,
            nomVendeur: (item as any).nom_vendeur || item.nomVendeur,
            projectId: (item as any).project_id || item.projectId,
            rawPayload: (item as any).raw_payload || item.rawPayload,
            createdAt: (item as any).created_at || item.createdAt,
            updatedAt: (item as any).updated_at || item.updatedAt
          }));
        }
        return response;
      })
    );
  }

  createAgendaProject(data: Partial<AgendaCas>): Observable<AgendaCas> {
    return this.http.post<AgendaCas>(this.apiUrl, data);
  }

  updateAgendaProject(id: number, data: Partial<AgendaCas>): Observable<AgendaCas> {
    return this.http.patch<AgendaCas>(`${this.apiUrl}/${id}`, data);
  }

  deleteAgendaProject(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  createProjectFromAgenda(agendaCasId: number): Observable<AgendaCasConversionResponse> {
    return this.http.post<AgendaCasConversionResponse>(`${this.apiUrl}/${agendaCasId}/create-project`, {});
  }

  exportAgendaProjectsExcel(
    agendaCasIds: number[] = [],
    options: { export_all_matching?: boolean; search?: string; status?: string; today_only?: boolean; user_id?: number; archived?: boolean } = {}
  ): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.apiUrl}/export/excel`, {
      agendaCasIds,
      ...options
    }, {
      observe: 'response',
      responseType: 'blob'
    });
  }
}
